import { DatePipe, PercentPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { WorkflowApiService, WorkflowIntentEvaluationRun, WorkflowIntentEvaluationSummary } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-evaluation-dashboard',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TagModule,
    DatePipe,
    PercentPipe
  ],
  template: `
    <ain-page [title]="t('workflow.intents.eval.dashboard.title')" [description]="t('workflow.intents.eval.dashboard.description')">
      <div class="toolbar">
        <input pInputText [(ngModel)]="datasetKey" [placeholder]="t('workflow.intents.eval.filters.datasetKey')" />
        <button pButton type="button" [label]="t('common.search')" (click)="reload()"></button>
        <button pButton type="button" severity="secondary" [label]="t('workflow.intents.eval.actions.openSimulation')" (click)="openSimulation()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else {
        <div class="metric-grid">
          <p-card [header]="t('workflow.intents.eval.metrics.classificationAccuracy')">
            <div class="metric-value">{{ summary()?.classificationAccuracyProxy ?? 0 | percent: '1.0-1' }}</div>
          </p-card>
          <p-card [header]="t('workflow.intents.eval.metrics.slotCompleteness')">
            <div class="metric-value">{{ summary()?.slotFillCompleteness ?? 0 | percent: '1.0-1' }}</div>
          </p-card>
          <p-card [header]="t('workflow.intents.eval.metrics.candidateHitRate')">
            <div class="metric-value">{{ summary()?.candidateSelectionHitRate ?? 0 | percent: '1.0-1' }}</div>
          </p-card>
          <p-card [header]="t('workflow.intents.eval.metrics.safetyMismatch')">
            <div class="metric-value">{{ summary()?.safetyMismatchCount ?? 0 }}</div>
          </p-card>
          <p-card [header]="t('workflow.intents.eval.metrics.executionSuccessRate')">
            <div class="metric-value">{{ summary()?.executionSuccessRate ?? 0 | percent: '1.0-1' }}</div>
          </p-card>
          <p-card [header]="t('workflow.intents.eval.metrics.executionFailureRate')">
            <div class="metric-value">{{ summary()?.executionFailureRate ?? 0 | percent: '1.0-1' }}</div>
          </p-card>
          <p-card [header]="t('workflow.intents.eval.metrics.clarificationRate')">
            <div class="metric-value">{{ summary()?.clarificationRate ?? 0 | percent: '1.0-1' }}</div>
          </p-card>
          <p-card [header]="t('workflow.intents.eval.metrics.approvalRate')">
            <div class="metric-value">{{ summary()?.approvalRate ?? 0 | percent: '1.0-1' }}</div>
          </p-card>
        </div>

        <p-card [header]="t('workflow.intents.eval.runs.title')">
          @if (runs().length === 0) {
            <ain-state type="empty" [title]="t('workflow.intents.eval.states.noRuns')" />
          } @else {
            <p-table [value]="runs()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('workflow.intents.eval.columns.datasetKey') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('workflow.intents.eval.columns.startedAt') }}</th>
                  <th>{{ t('workflow.intents.eval.columns.completedAt') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-run>
                <tr>
                  <td>{{ run.datasetKey }}</td>
                  <td><p-tag [value]="run.status"></p-tag></td>
                  <td>{{ run.startedAtUtc | date: 'short' }}</td>
                  <td>{{ run.completedAtUtc ? (run.completedAtUtc | date: 'short') : '-' }}</td>
                  <td>
                    <button pButton type="button" text [label]="t('common.open')" (click)="openRun(run)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </p-card>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; flex-wrap: wrap; }
    .metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 12px; margin-bottom: 12px; }
    .metric-value { font-size: 1.3rem; font-weight: 600; }
  `]
})
export class WorkflowIntentEvaluationDashboardComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly summary = signal<WorkflowIntentEvaluationSummary | null>(null);
  readonly runs = signal<WorkflowIntentEvaluationRun[]>([]);

  datasetKey = '';

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);

    this.api.getIntentEvaluationMetrics({ datasetKey: this.datasetKey || undefined }).subscribe({
      next: summary => {
        this.summary.set(summary);
        this.api.listIntentEvaluationRuns({
          datasetKey: this.datasetKey || undefined,
          pageNumber: 1,
          pageSize: 20
        }).subscribe({
          next: runs => {
            this.runs.set(runs.items ?? []);
            this.loading.set(false);
          },
          error: () => {
            this.loading.set(false);
            this.error.set(this.t('common.loadFailed'));
          }
        });
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('common.loadFailed'));
      }
    });
  }

  openRun(run: WorkflowIntentEvaluationRun): void {
    void this.router.navigate(['/workflow/intents/evaluation/runs', run.id]);
  }

  openSimulation(): void {
    void this.router.navigate(['/workflow/intents/evaluation/simulation']);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
